import Alert from "@mui/material/Alert";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { useAuditIdentityOptions } from "@/shared/components/principal";
import {
  QuickSearch,
  QuickSearchButton,
  ResourceView,
  activeFiltersToQuerySubmission,
  toOptionsResolver,
  useQuickSearchHotkey,
  useResourceView,
  ViewPicker,
  SaveViewControl,
  useLoadedView,
  useRecordTableState,
  useResourceTableContext,
  useRestoredTableState,
  useViewFromLink,
  type FiltersHandle,
  type FilterDefinition,
  type QuickSearchProperty,
  type RoboqlMenuConfig,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import {
  CustomFieldRemovedColumnsAlert,
  useCustomFieldGridIntegration,
} from "@/shared/components/ResourceView/customFields";
import {
  EVENT_FILTER_FIELDS,
  hydrateFilterDefinitions,
} from "@/shared/components/ResourceView/Filters/catalog";
import { TargetEntityType } from "@/shared/domain/customFields";
import {
  eventScopeToCondition,
  type EventQueryScope,
} from "@/shared/domain/events/EventQueryScope";
import { EventRecord } from "@/shared/domain/events/EventRecord";
import { eventsTableColumns } from "@/shared/domain/events/EventsTableColumns";
import {
  eventQueryKeys,
  useEventMetadataKeys,
  useEventTagSearch,
} from "@/shared/domain/events/hooks";
import {
  composeQueryBody,
  formatMetadataValue,
  QueryContentMode,
  QueryTarget,
} from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useEventTablePreferences } from "@/shared/domain/users/hooks";
import { ENTITY_ICONS } from "@/shared/foundation/icons";
import {
  ColumnVisibility,
  DataTable,
  FLEX_FILL_COL_DEF,
  buildColumnsForPreferences,
  colId,
  renderDashOrValue,
  type ColDef,
  type ColumnPickerOption,
  deriveLoadingState,
} from "@/shared/foundation/primitives/Table";

import { EventDetails } from "./EventDetails";
import styles from "./EventsList.module.css";

const STATIC_EVENT_PICKER_OPTIONS: ColumnPickerOption[] =
  eventsTableColumns.map((c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }));

const ROBOQL_CONFIG: RoboqlMenuConfig = { target: QueryTarget.Events };
// Declared beside the config that makes the filter bar RoboQL-capable, so the
// two cannot drift: a list that hands `ROBOQL_CONFIG` to its filters must also
// let a RoboQL View be applied.
const VIEW_LOAD_OPTIONS = { roboql: true } as const;

// Quick find routes a typed value to one field: an `ev_`-shaped value to the
// id, everything else to the name. Categorical filters (tags) live in the
// Filter menu, not here.
const QUICK_SEARCH_PROPERTIES: QuickSearchProperty[] = [
  {
    field: "event_id",
    name: "ID",
    // An event id is `ev_` plus non-space characters — anchored so a phrase
    // merely starting that way falls through to the name search.
    pattern: /^ev_\S+$/,
  },
  { field: "name", name: "Name", fallback: true },
];

interface EventsListProps {
  scope: EventQueryScope;
  selectionOptions?: SelectionOption<EventRecord>[];
}

export function EventsList({ scope, selectionOptions }: EventsListProps) {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const quickSearchState = useQuickSearchHotkey();
  const { preferences, savePreferences } = useEventTablePreferences();
  const metadataKeysQuery = useEventMetadataKeys(resolvedOrgId);

  // Directories backing the `created_by` / `modified_by` identity filters.
  const identityOptions = useAuditIdentityOptions(resolvedOrgId);

  // Capture initial columns preferences once at mount
  const [initialColumnIds] = React.useState(() => preferences.columns);

  const customFields = useCustomFieldGridIntegration<EventRecord>({
    entityType: TargetEntityType.Event,
    preferenceOrder: initialColumnIds,
    savePreferences,
  });

  // Custom-field columns come before metadata columns (after the static set).
  const columns = React.useMemo<ColDef<EventRecord>[]>(
    () =>
      buildColumnsForPreferences<EventRecord>({
        staticColumns: eventsTableColumns,
        dynamicColumns: [
          ...customFields.customFieldColumns,
          ...(metadataKeysQuery.data ?? []).map<ColDef<EventRecord>>((key) => ({
            colId: `metadata.${key}`,
            headerName: key,
            // Floor so a flex metadata column doesn't collapse on overflow.
            minWidth: 120,
            valueGetter: (p) => p.data?.metadata?.[key],
            valueFormatter: (p) => formatMetadataValue(p.value),
            cellRenderer: renderDashOrValue,
            sortable: false,
          })),
        ],
        preferenceOrder: customFields.effectivePreferenceOrder,
      }),
    [
      customFields.customFieldColumns,
      customFields.effectivePreferenceOrder,
      metadataKeysQuery.data,
    ],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...STATIC_EVENT_PICKER_OPTIONS,
      ...customFields.columnPickerOptions,
      ...(metadataKeysQuery.data ?? []).map<ColumnPickerOption>((key) => ({
        id: `metadata.${key}`,
        label: key,
      })),
    ],
    [customFields.columnPickerOptions, metadataKeysQuery.data],
  );

  // A `?view=` link is resolved in the route loader, so the View it names
  // is available here — before the table's first query is composed. Seeding
  // it is what keeps a shared link from rendering an unfiltered page first.
  const linked = useViewFromLink<EventRecord>({
    getRowId: (row) => row.event_id,
    initialSort: { columnId: "start_time", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
  });
  const restored = useRestoredTableState({
    target: QueryTarget.Events,
    link: linked,
  });
  const view = useResourceView<EventRecord>(restored.options);

  // Which saved View is applied, and the routes in and out of one.
  const viewSession = useLoadedView(view, restored.view, VIEW_LOAD_OPTIONS);
  useRecordTableState({
    target: QueryTarget.Events,
    view,
    loadedView: viewSession.loadedView,
  });
  useResourceTableContext({
    target: QueryTarget.Events,
    view,
    session: viewSession,
  });
  // Lets the View picker's empty state open the filter menu on a field.
  const filtersRef = React.useRef<FiltersHandle>(null);

  const searchEventTags = useEventTagSearch(resolvedOrgId);

  const filterDefinitions: FilterDefinition[] = React.useMemo(
    () => [
      ...hydrateFilterDefinitions(EVENT_FILTER_FIELDS, {
        identityOptions,
        setOptions: toOptionsResolver(searchEventTags),
      }),
      ...customFields.filterDefinitions,
    ],
    [customFields.filterDefinitions, identityOptions, searchEventTags],
  );

  const submission = activeFiltersToQuerySubmission({
    filters: view.filters,
    sort: view.table.sort,
  });

  const query = composeQueryBody({
    scopeCondition: eventScopeToCondition(scope),
    filterCondition: submission.filterCondition,
    sort: submission.sort,
    maxResults: submission.maxResults,
  });

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Events,
    // Events ship with metadata
    contentMode: QueryContentMode.RecordWithMeta,
    queryKey: eventQueryKeys.list(resolvedOrgId, scope, query),
  });
  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<EventRecord>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );
  const countQuery = useQueryCount(queryId);

  const rows = pageQuery.data ?? [];

  const renderExpandedRow = React.useCallback(
    (row: EventRecord) => <EventDetails event={row} readonly={false} />,
    [],
  );

  const loading = deriveLoadingState({
    isLoading: submitQuery.isLoading || pageQuery.isLoading,
    isFetching: submitQuery.isFetching || pageQuery.isFetching,
  });
  const fetchError = submitQuery.error ?? pageQuery.error;

  const hasSelectionOptions = Boolean(selectionOptions?.length);

  return (
    <>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <CustomFieldRemovedColumnsAlert {...customFields.removedColumnsAlert} />
      <QuickSearch
        open={quickSearchState.isOpen}
        onClose={quickSearchState.close}
        properties={QUICK_SEARCH_PROPERTIES}
        placeholder={"Search by ID or name…"}
        onSubmit={(filter) => {
          view.clearFilters();
          view.commit({ type: "structured", filter });
        }}
      />
      <ResourceView.Root>
        <ResourceView.Actions>
          <ViewPicker
            target={QueryTarget.Events}
            loadedView={viewSession.loadedView}
            onSelectView={viewSession.selectView}
            seededViewId={viewSession.seededViewId}
            arrivalClaimed={restored.arrivalClaimed}
            onClearView={viewSession.clearView}
            linkFor={viewSession.linkFor}
            filterDefinitions={filterDefinitions}
            onStartFilter={(field) => filtersRef.current?.openWithField(field)}
          />
          <QuickSearchButton
            onClick={quickSearchState.open}
            label="Find events"
          />
          <ResourceView.Filters
            ref={filtersRef}
            filterDefinitions={filterDefinitions}
            filters={view.filters}
            onFilterSelected={view.commit}
            roboql={ROBOQL_CONFIG}
          />
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          <ResourceView.Selection<EventRecord>
            selection={view.table.selection}
            clearSelection={view.table.clearSelection}
            options={selectionOptions ?? []}
          />
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          trailingActions={
            <SaveViewControl
              view={view}
              target={QueryTarget.Events}
              session={viewSession}
            />
          }
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
          roboql={ROBOQL_CONFIG}
        />
        <DataTable<EventRecord>
          defaultColDef={FLEX_FILL_COL_DEF}
          table={view.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent={
            // Events are created from a dataset or the visualizer, never from
            // this list, so the empty org sees where to go rather than a
            // button.
            <ResourceView.EmptyState
              hasActiveFilters={view.hasActiveFilters}
              noData={{
                icon: ENTITY_ICONS.event,
                title: "No events yet",
                description:
                  "Mark a moment of interest from a dataset or the visualizer.",
              }}
              noMatches={{
                icon: ENTITY_ICONS.event,
                title: "No matching events",
                description: "Try adjusting or clearing your filters.",
              }}
            />
          }
          totalRowCount={countQuery.data}
          selectionEnabled={hasSelectionOptions}
          expansion={{ renderRow: renderExpandedRow }}
        />
      </ResourceView.Root>
    </>
  );
}
